/**
 * Sidenav — the nested primary sidebar.
 *
 * Layout:
 *   sidenav-logo            (Deepdub mark — always navigates home)
 *   sidenav-levels          (all levels mounted; the active one is shown)
 *   sidenav-foot            (user card with menu — always visible)
 *
 * This component is mounted once by `ShellLayout` and stays mounted across
 * navigation — it never remounts. The middle holds **every level at once**
 * (`home` / `admin` / `bot` / `call-logs` / `config` / `operate` / `workflow`,
 * see `nav/sidebarLevel.ts`); the level matching the current URL (`useLocation`)
 * is shown initially, while breadcrumb clicks can walk the sidebar back without
 * changing the page. The active level is translated to 0/opacity 1,
 * the others are pushed off to ±100px/opacity 0 (left if shallower than the
 * active level, right if deeper) and animate via a CSS `transition`. So moving
 * to a submenu slides the old content left and the new content in from the
 * right; going back reverses it. Nothing mounts or unmounts, so the transition
 * is always smooth.
 *
 * Notes:
 *   - User card is wired to `useAuthnMe()` (real email + roles + logout).
 *   - Theme toggle goes through `useTheme()` (writes body.dataset.theme).
 *   - For unauthenticated visitors the levels are hidden and the footer
 *     renders a single Sign-in button (no workspace nav makes sense).
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useAuthnMe } from '../../AuthnMeProvider';
import { useNavTree } from '../nav/useNavTree';
import { findPath, findPathByIdOrPage, NavNode } from '../nav/tree';
import { navigate as routerNavigate } from '../nav/useUrl';
import SpaLink from '../nav/SpaLink';
import {
  botSectionHref,
  LEVEL_DEPTH,
  sidebarScopeForPath,
  type SidebarLevel,
} from '../nav/sidebarLevel';
import {
  orderWorkflowsForNav,
  useWorkflowNav,
  workflowEditorHref,
} from '../nav/workflowNav';
import { sortByRecency, useRecentBots } from '../nav/recentBots';
import { botTint } from '../nav/botTint';
import { useBotDraftState } from '../data/draftStorage';
import {
  callLogsHref,
  hasActiveCallLogsFilters,
  parseCallLogsParams,
  type CallLogsParams,
} from '../nav/callLogsUrl';
import { useBotFlags } from '../nav/useBotFlags';
import {
  ADMIN_SECTIONS,
  adminSectionFromSearch,
  adminSectionHref,
} from '../nav/adminSections';
import {
  AudioLines,
  BarChart3,
  BookOpen,
  Bot,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Code2,
  CreditCard,
  Ellipsis,
  FlaskConical,
  Home,
  LayoutDashboard,
  ListChecks,
  Network,
  Package as PackageIcon,
  Phone,
  PhoneCall,
  PhoneOff,
  Plus,
  RadioTower,
  Settings,
  ShieldUser,
  Share2,
  Users,
  Wrench,
  X,
  type LucideIcon,
  MessagesSquare,
  Ear,
} from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import { setPreference } from '../../preferences';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { HOME_PATH } from '../../utils/homePath';
import DeepdubLogo from './DeepdubLogo';

interface SidenavProps {
  /** Mobile drawer open state — adds the `open` class so CSS slides it in.
   *  No effect on desktop (the sidenav is a static grid column there). */
  open?: boolean;
}

const ALL_LEVELS: SidebarLevel[] = ['home', 'admin', 'bot', 'call-logs', 'config', 'operate', 'workflow'];

/** How many bots the home level shows before collapsing the rest behind "Show more". */
const MAX_HOME_BOTS = 5;

/** Sidebar panel opened by each row that drills into another level. */
const DRILL_LEVEL: Partial<Record<string, SidebarLevel>> = {
  'bot:call-logs': 'call-logs',
  'bot:operate': 'operate',
  'bot:settings': 'config',
  'bot:workflow': 'workflow',
};

function displayName(email: string | undefined): string {
  if (!email) return 'You';
  const local = email.split('@')[0];
  if (!local) return email;
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((s) => s[0].toUpperCase() + s.slice(1))
    .join(' ') || email;
}

const NAV_ICONS: Record<string, LucideIcon> = {
  home: Home,
  bots: Home,
  'bot:overview': LayoutDashboard,
  'bot:dial': Phone,
  'bot:call-logs': ListChecks,
  'bot:operate': RadioTower,
  'bot:operate-campaigns': RadioTower,
  'bot:dnc': PhoneOff,
  'bot:settings': Settings,
  'bot:help': BookOpen,
  'bot:api-docs': Code2,
  team: Users,
  billing: CreditCard,
  packages: PackageIcon,
  help: BookOpen,
  'api-docs': Code2,
  'call-logs:calls': ListChecks,
  'call-logs:analytics': BarChart3,
  'call-logs:users': Users,
};

/** Icons for the Settings submenu's section dividers (the individual items
 *  under them are label-only). Keyed by the `section` string set in `tree.ts`. */
const SECTION_ICONS: Record<string, LucideIcon> = {
  'Voice & behavior': AudioLines,
  Calls: PhoneCall,
  Chat: MessagesSquare,
  Passive: Ear,
  Setup: Wrench,
  Advanced: FlaskConical,
};

/** Icon for a nav row. Rows with no mapping (e.g. secondary settings items)
 *  render a same-width empty spacer so their labels stay aligned. */
function NavGlyph({ id }: { id: string }) {
  const Icon = NAV_ICONS[id];
  if (!Icon) return <span className="nav-ico nav-ico-empty" aria-hidden="true" />;
  return <Icon className="nav-ico" aria-hidden="true" strokeWidth={1.9} />;
}

interface Crumb {
  id: string;
  icon: LucideIcon;
  label: string;
  href: string;
  /** Sidebar panel shown when this crumb is selected. */
  level: SidebarLevel;
  /** Override the crumb icon's size in px (defaults to the 16px `.nav-ico`). */
  iconSize?: number;
  /** When set, a chevron-down after the crumb opens a bot-switch dropdown.
   *  `hrefFor` is where each option points — a real href, so Cmd/Ctrl-click
   *  opens that bot in a new tab instead of switching this one. */
  switcher?: {
    bots: NavNode[];
    onPick: (botId: string) => void;
    hrefFor: (botId: string) => string;
  };
}

/** A single breadcrumb link (icon + label). The label is collapsed (max-width
 *  0) unless it's the last crumb, hovered (`.crumb:hover`), or force-expanded
 *  via `hover` (used by SwitcherCrumb). */
function CrumbLink({
  crumb, last, hover = false, onClick, onMouseEnter, onMouseLeave,
}: {
  crumb: Crumb;
  last: boolean;
  hover?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const Icon = crumb.icon;
  return (
    <SpaLink
      href={crumb.href}
      className={'crumb' + (last ? ' is-last' : '') + (hover ? ' is-hover' : '')}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Icon
        className="nav-ico"
        aria-hidden="true"
        strokeWidth={1.9}
        style={crumb.iconSize ? { width: crumb.iconSize, height: crumb.iconSize, flex: `0 0 ${crumb.iconSize}px` } : undefined}
      />
      <span className="crumb-label">{crumb.label}</span>
    </SpaLink>
  );
}

/**
 * The bot crumb + its switcher chevron. The label expands on hover, but the
 * chevron is a separate element outside the crumb link, so moving the mouse
 * onto it would normally drop `:hover` and collapse the label. We bridge that
 * gap in code: hovering the label sets `wasHoveredRecently`; for a 20ms window
 * after leaving the label, entering the chevron re-asserts the hover state so
 * the label stays open. (20ms is far shorter than the label's 260ms max-width
 * transition, so the handoff is invisible.)
 */
/** How long the bot name stays visible after switching bots. */
const BOT_SWITCH_REVEAL_MS = 2500;

function SwitcherCrumb({
  crumb,
  last,
  onClick,
}: {
  crumb: Crumb;
  last: boolean;
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
}) {
  const [isHover, setIsHover] = useState(false);
  const wasHoveredRecently = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const revealTimer = useRef<number | undefined>(undefined);
  const clearTimer = () => {
    if (timer.current !== undefined) {
      window.clearTimeout(timer.current);
      timer.current = undefined;
    }
  };
  const clearReveal = () => {
    if (revealTimer.current !== undefined) {
      window.clearTimeout(revealTimer.current);
      revealTimer.current = undefined;
    }
  };
  // Clear pending timeouts on unmount (refs are stable).
  useEffect(() => () => {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    if (revealTimer.current !== undefined) window.clearTimeout(revealTimer.current);
  }, []);

  // Switching bots reveals the (new) bot name for a moment before it collapses
  // back — the sidebar stays mounted across the navigation, so this instance
  // just re-renders with the new label.
  const handlePick = (id: string) => {
    clearReveal();
    setIsHover(true);
    revealTimer.current = window.setTimeout(() => {
      setIsHover(false);
      revealTimer.current = undefined;
    }, BOT_SWITCH_REVEAL_MS);
    crumb.switcher!.onPick(id);
  };

  return (
    <span className="crumb-group">
      <CrumbLink
        crumb={crumb}
        last={last}
        hover={isHover}
        onClick={onClick}
        onMouseEnter={() => { clearTimer(); clearReveal(); wasHoveredRecently.current = true; setIsHover(true); }}
        onMouseLeave={() => {
          setIsHover(false);
          clearTimer();
          timer.current = window.setTimeout(() => {
            wasHoveredRecently.current = false;
            timer.current = undefined;
          }, 20);
        }}
      />
      <span
        className="crumb-switch-wrap"
        onMouseEnter={() => { if (wasHoveredRecently.current) setIsHover(true); }}
        onMouseLeave={() => setIsHover(false)}
      >
        <BotPickerPopover
          bots={crumb.switcher!.bots}
          onPick={handlePick}
          hrefFor={crumb.switcher!.hrefFor}
          triggerClassName="crumb-switch"
          triggerAriaLabel="Switch bot"
        >
          <ChevronDown className="nav-ico" aria-hidden="true" strokeWidth={2.2} />
        </BotPickerPopover>
      </span>
    </span>
  );
}

/**
 * Breadcrumb trail for a level. Each crumb is an icon + a label; the label is
 * collapsed (max-width 0) unless it's the last crumb or hovered, and animates
 * open/closed via a max-width transition (see `.crumb-label` in soft.css). The
 * label text stays in the DOM while collapsed, so the link keeps its accessible
 * name. The bot crumb (with a `switcher`) also carries the switch chevron.
 */
function Breadcrumbs({
  crumbs,
  activeLevel,
  onLevelChange,
}: {
  crumbs: Crumb[];
  activeLevel: SidebarLevel;
  onLevelChange: (level: SidebarLevel) => void;
}) {
  const handleClick = (crumb: Crumb): React.MouseEventHandler<HTMLAnchorElement> => (event) => {
    // Breadcrumbs control the sidebar only. Home becomes a page link once the
    // sidebar is already at its root, which gives a second Home click the
    // explicit "return to /customer" behavior.
    if (crumb.level === 'home' && activeLevel === 'home') return;
    event.preventDefault();
    onLevelChange(crumb.level);
  };

  return (
    <div className="sidenav-crumbs" data-testid="sidenav-crumbs">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <React.Fragment key={c.id}>
            {i > 0 && <span className="crumb-sep" aria-hidden="true">/</span>}
            {c.switcher
              ? <SwitcherCrumb crumb={c} last={last} onClick={handleClick(c)} />
              : <CrumbLink crumb={c} last={last} onClick={handleClick(c)} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function findNode(root: NavNode, id: string): NavNode | undefined {
  const path = findPath(root, id);
  return path ? path[path.length - 1] : undefined;
}

/** Off-screen placement for an inactive level, relative to the active one. */
function levelStyle(level: SidebarLevel, active: SidebarLevel): React.CSSProperties {
  if (level === active) return { transform: 'translateX(0)', opacity: 1 };
  const behind = LEVEL_DEPTH[level] < LEVEL_DEPTH[active];
  return {
    transform: `translateX(${behind ? '-100px' : '100px'})`,
    opacity: 0,
    pointerEvents: 'none',
  };
}

export default function Sidenav({ open }: SidenavProps) {
  const nav = useNavTree();
  const theme = useTheme();
  const authn = useAuthnMe();
  const location = useLocation();
  const recent = useRecentBots();
  const isAuth = !!authn?.isAuthenticated;
  const email = authn?.user?.email;
  const name = displayName(email);
  const initial = (name[0] || '?').toUpperCase();
  const roles = authn?.user?.roles || [];
  const isAdmin = roles.includes('admin') || roles.includes('owner');
  const subtitle = isAdmin
    ? 'Platform admin'
    : roles.includes('reseller') ? 'Reseller' : 'Member';

  const [menuOpen, setMenuOpen] = useState(false);
  // Home level: whether the bot list is expanded past the first MAX_HOME_BOTS.
  const [showAllBots, setShowAllBots] = useState(false);

  const themeLabel = theme.theme[0].toUpperCase() + theme.theme.slice(1);
  const setThemeChoice = (next: 'system' | 'light' | 'dark') => {
    theme.setTheme(next);
    setMenuOpen(false);
  };

  // A route change chooses the matching sidebar panel. Breadcrumb clicks may
  // then walk only the sidebar back through its ancestors, leaving that route
  // (and therefore the main page) untouched.
  const routeLevel: SidebarLevel = sidebarScopeForPath(location.pathname, location.search).level;
  const [activeLevel, setActiveLevel] = useState<SidebarLevel>(routeLevel);
  useEffect(() => {
    setActiveLevel(routeLevel);
  }, [location.pathname, location.search, location.hash, routeLevel]);
  const botId = nav.botId;
  const clParams = parseCallLogsParams(location.search);
  const { memoryEnabled } = useBotFlags(botId);

  // For active-item highlighting we follow the pageId trail.
  const pagePath = findPathByIdOrPage(nav.tree, nav.pageId) ?? [];
  const pageIds = pagePath.map((n) => n.id);

  const workspaceNodes = nav.tree.children || [];
  const botsGroup = workspaceNodes.find((n) => n.id === 'bots');
  const botNodes = sortByRecency(
    (botsGroup?.children || []).filter((n) => n.kind === 'bot'),
    (n) => n.id.replace(/^bot:/, ''),
    recent,
  );
  const currentBotNode = botId ? findNode(nav.tree, `bot:${botId}`) : undefined;
  const botNavItems = currentBotNode?.children || [];
  const settingsItems = botNavItems.find((n) => n.id === 'bot:settings')?.children || [];
  const operateItems = botNavItems.find((n) => n.id === 'bot:operate')?.children || [];
  const workspaceLinks = workspaceNodes.filter((n) => n.id !== 'bots');
  const currentBotLabel = currentBotNode?.label || 'Select bot';
  const showStartBotButton = !nav.botsLoading && botNodes.length === 0;

  const isActive = (it: NavNode) => (
    nav.pageId === it.id
    || (it.kind === 'leaf' && it.page === nav.pageId)
    || pageIds.includes(it.id)
  );

  const renderNavLink = (it: NavNode, opts: { nested?: boolean; drills?: boolean } = {}) => {
    const { nested = false, drills = false } = opts;
    const href = it.to?.({ botId, customerId: nav.customerId }) || HOME_PATH;
    const drillLevel = drills ? DRILL_LEVEL[it.id] : undefined;
    return (
      <SpaLink
        key={it.id}
        href={href}
        className={'nav-link main-nav-link' + (nested ? ' nested' : '') + (isActive(it) ? ' active' : '')}
        onClick={() => {
          // The main page may already be at this href after a breadcrumb walked
          // only the sidebar upward, so reopen the child panel explicitly.
          if (drillLevel) setActiveLevel(drillLevel);
        }}
      >
        {!nested && <NavGlyph id={it.id} />}
        <span className="nav-label">{it.label}</span>
        {it.count ? <span className="ct">{it.count}</span> : null}
        {drills && (
          <ChevronRight className="nav-drill-chevron" aria-hidden="true" strokeWidth={2.2} />
        )}
      </SpaLink>
    );
  };

  /** Section-divider-aware list (the Settings submenu groups this way). Each
   *  section divider carries an icon (see SECTION_ICONS); the items under it are
   *  label-only (rendered `nested`, so no per-item glyph). */
  const renderNavList = (items: NavNode[], nested = false) => {
    let lastSection: string | undefined;
    return items.map((it) => {
      const showSection = !!it.section && it.section !== lastSection;
      if (it.section) lastSection = it.section;
      const SectionIcon = it.section ? SECTION_ICONS[it.section] : undefined;
      return (
        <React.Fragment key={it.id}>
          {showSection && (
            <div className="nav-section">
              {SectionIcon && <SectionIcon className="nav-section-ico" aria-hidden="true" strokeWidth={1.9} size={20}/>}
              <span>{it.section}</span>
            </div>
          )}
          {/* Workflow is the one settings row that opens a level of its own
              (the bot's conversation workflows), so it carries a chevron. */}
          {renderNavLink(it, { nested, drills: it.id === 'bot:workflow' })}
        </React.Fragment>
      );
    });
  };

  // Switching bots keeps the section the user is in (call-logs → call-logs,
  // config?tab=… → same tab, …). See botSectionHref.
  const botSwitchHref = (id: string) => botSectionHref(location.pathname, location.search, id);
  const pickBot = (id: string) => {
    void setPreference('selectedBotId', id).catch(() => {});
    routerNavigate(botSwitchHref(id));
  };

  // Breadcrumb trail per level — the way up out of a nested level. Each crumb
  // is an icon + a label; the label collapses to an icon unless it's the last
  // crumb or hovered (animated via max-width in soft.css). The bot crumb shows
  // the current bot's name and carries a chevron that opens the bot switcher.
  const homeCrumb: Crumb = { id: 'home', icon: Home, label: 'Home', href: HOME_PATH, level: 'home' };
  const botCrumb: Crumb = {
    id: 'bot',
    icon: Bot,
    label: currentBotLabel,
    href: `/b/${botId}/manage`,
    level: 'bot',
    iconSize: 19,
    switcher: botNodes.length > 0
      ? { bots: botNodes, onPick: pickBot, hrefFor: botSwitchHref }
      : undefined,
  };
  const settingsCrumb: Crumb = {
    id: 'settings',
    icon: Settings,
    label: 'Settings',
    href: `/b/${botId}/config`,
    level: 'config',
  };
  const crumbsFor: Record<SidebarLevel, Crumb[]> = {
    home: [homeCrumb],
    admin: [homeCrumb, {
      id: 'admin', icon: ShieldUser, label: 'Admin', href: '/admin', level: 'admin', iconSize: 18,
    }],
    bot: [homeCrumb, botCrumb],
    config: [homeCrumb, botCrumb, settingsCrumb],
    'call-logs': [homeCrumb, botCrumb, {
      id: 'calls', icon: Phone, label: 'Calls', href: `/b/${botId}/call-logs`, level: 'call-logs',
    }],
    operate: [homeCrumb, botCrumb, {
      id: 'operate', icon: RadioTower, label: 'Outbound', href: `/b/${botId}/operate`, level: 'operate',
    }],
    workflow: [
      homeCrumb,
      botCrumb,
      settingsCrumb,
      {
        id: 'workflows',
        icon: Share2,
        label: 'Workflows',
        href: workflowEditorHref(botId || ''),
        level: 'workflow',
      },
    ],
  };

  // A bot row in the home list. Selecting one collapses the "show more" list so
  // it isn't left expanded when the user returns to home.
  const renderBotRow = (bot: NavNode) => {
    const id = bot.id.replace(/^bot:/, '');
    return (
      <SpaLink
        key={bot.id}
        href={`/b/${id}/manage`}
        className="nav-link main-nav-link"
        onClick={() => {
          void setPreference('selectedBotId', id).catch(() => {});
          setShowAllBots(false);
          // Home may be a breadcrumb-only view over this bot's existing page,
          // so selecting the same bot must reopen its panel without relying on
          // a route change.
          setActiveLevel('bot');
        }}
      >
        <span className={('nav-av ' + botTint(id)).trim()} aria-hidden="true">{bot.avatar || 'B'}</span>
        <span className="nav-label">{bot.label}</span>
        <BotDraftDot botId={id} />
        <ChevronRight className="nav-drill-chevron" aria-hidden="true" strokeWidth={2.2} />
      </SpaLink>
    );
  };

  // ── Level bodies ────────────────────────────────────────────────────
  const homeLevel = (
    <div className="sidenav-main">
      <div className="sidenav-scrollable-area">
        {showStartBotButton && (
          <button
            type="button"
            className="bot-start-button"
            onClick={() => routerNavigate(`${HOME_PATH}#new-bot`)}
          >
            Start a new bot
          </button>
        )}
        {botNodes.length > 0 && (
          <nav className="sidenav-items main-nav-items bot-nav-items" aria-label="Your bots">
            <div className="nav-section top-level">Your bots</div>
            {botNodes.slice(0, MAX_HOME_BOTS).map(renderBotRow)}
            {botNodes.length > MAX_HOME_BOTS && (
              <>
                {/* The overflow bots live in a collapsible wrapper that animates
                    its height (max-height 0 → rows × 40px). */}
                <div
                  className="sidenav-more-bots"
                  data-testid="sidenav-more-bots"
                  style={{ maxHeight: `${showAllBots ? (botNodes.length - MAX_HOME_BOTS) * 40 : 0}px` }}
                >
                  {botNodes.slice(MAX_HOME_BOTS).map(renderBotRow)}
                </div>
                <button
                  type="button"
                  className="nav-link main-nav-link sidenav-more-btn"
                  aria-expanded={showAllBots}
                  onClick={() => setShowAllBots((v) => !v)}
                >
                  <span className="nav-label">
                    {showAllBots ? 'Show less' : `Show more (${botNodes.length - MAX_HOME_BOTS})`}
                  </span>
                  {showAllBots
                    ? <ChevronUp className="nav-drill-chevron" aria-hidden="true" strokeWidth={2.2} />
                    : <ChevronDown className="nav-drill-chevron" aria-hidden="true" strokeWidth={2.2} />}
                </button>
              </>
            )}
          </nav>
        )}
        <div className="flex-1" />
        {workspaceLinks.length > 0 && (
          <nav className="sidenav-items main-nav-items workspace-nav-items" aria-label="Workspace navigation">
            <div className="nav-section top-level">Workspace tools</div>
            {workspaceLinks.map((it) => renderNavLink(it))}
          </nav>
        )}
      </div>
    </div>
  );

  const botLevel = (
    <div className="sidenav-main">
      <div className="sidenav-scrollable-area">
        <nav className="sidenav-items main-nav-items bot-nav-items" aria-label="Current bot navigation">
          {botNavItems.map((it) => renderNavLink(it, {
            drills: it.id === 'bot:settings' || it.id === 'bot:call-logs' || it.id === 'bot:operate',
          }))}
        </nav>
      </div>
    </div>
  );

  const configLevel = (
    <div className="sidenav-main">
      <div className="sidenav-scrollable-area">
        <nav className="sidenav-items main-nav-items" aria-label="Bot settings navigation">
          {renderNavList(settingsItems, true)}
        </nav>
      </div>
    </div>
  );

  const operateLevel = (
    <div className="sidenav-main">
      <div className="sidenav-scrollable-area">
        {/* Not `nested`: the two rows carry their own glyphs (NAV_ICONS
            'bot:operate-campaigns' / 'bot:dnc') the way the call-logs View rows
            do — unlike the Settings submenu, where the icons sit on the section
            dividers and the items under them are label-only. */}
        <nav className="sidenav-items main-nav-items" aria-label="Outbound navigation">
          {renderNavList(operateItems)}
        </nav>
      </div>
    </div>
  );

  const workflowLevel = (
    <div className="sidenav-main">
      <div className="sidenav-scrollable-area">
        <WorkflowLevelBody botId={botId || ''} />
      </div>
    </div>
  );

  const callLogsLevel = (
    <div className="sidenav-main">
      <div className="sidenav-scrollable-area">
        <CallLogsLevelBody botId={botId || ''} params={clParams} memoryEnabled={memoryEnabled} />
      </div>
    </div>
  );

  const adminLevel = (
    <div className="sidenav-main">
      <div className="sidenav-scrollable-area">
        <AdminLevelBody pathname={location.pathname} search={location.search} />
      </div>
    </div>
  );

  const LEVEL_BODY: Record<SidebarLevel, React.ReactNode> = {
    home: homeLevel,
    admin: adminLevel,
    bot: botLevel,
    'call-logs': callLogsLevel,
    config: configLevel,
    operate: operateLevel,
    workflow: workflowLevel,
  };

  return (
    <aside className={['sidenav', open ? 'open' : ''].filter(Boolean).join(' ')}>
      <div className="sidenav-logo">
        <Link
          className="sidenav-logo-btn"
          to={HOME_PATH}
          aria-label="Go to Your Bots"
          title="Your Bots"
        >
          <DeepdubLogo />
        </Link>
      </div>

      {/* Breadcrumbs are persistent chrome — rendered once below the logo, not
          inside the sliding levels. The trail follows the active sidebar panel;
          the bot crumb carries the bot switcher (chevron → dropdown). */}
      {isAuth && (
        <Breadcrumbs
          crumbs={crumbsFor[activeLevel]}
          activeLevel={activeLevel}
          onLevelChange={setActiveLevel}
        />
      )}

      {isAuth && (
        <div className="sidenav-levels">
          {ALL_LEVELS.map((lvl) => (
            <div
              key={lvl}
              className={'sidenav-level' + (lvl === activeLevel ? ' active' : '')}
              data-testid="sidenav-level"
              data-active={lvl === activeLevel ? 'true' : undefined}
              style={levelStyle(lvl, activeLevel)}
              aria-hidden={lvl === activeLevel ? undefined : true}
            >
              {LEVEL_BODY[lvl]}
            </div>
          ))}
        </div>
      )}

      <div className="sidenav-foot">
        {isAuth ? (
          <div className="sidenav-user-wrap">
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="sidenav-user outline-none"
                >
                  <div className="av">{initial}</div>
                  <div className="sidenav-user-copy flex-1">
                    <b>{name}</b>
                    <span>{subtitle}</span>
                  </div>
                  <ChevronsUpDown size={16} color="var(--ink-3)"/>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="sidenav-user-menu"
                side="top"
                align="start"
                sideOffset={8}
              >
                {email && (
                  <DropdownMenuLabel className="sidenav-user-menu-email">
                    {email}
                  </DropdownMenuLabel>
                )}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="sidenav-submenu-trigger">
                    <span>Theme</span>
                    <span className="sidenav-menu-value">{themeLabel}</span>
                    <span className="sidenav-menu-arrow">›</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    className="sidenav-submenu-panel"
                    sideOffset={8}
                    alignOffset={-6}
                  >
                    <DropdownMenuRadioGroup
                      value={theme.theme}
                      onValueChange={(choice) => setThemeChoice(choice as 'system' | 'light' | 'dark')}
                    >
                      {(['system', 'light', 'dark'] as const).map((choice) => (
                        <DropdownMenuRadioItem
                          key={choice}
                          value={choice}
                          className="sidenav-submenu-item"
                        >
                          {choice[0].toUpperCase() + choice.slice(1)}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                {/* A real link (`asChild`), so Cmd/Ctrl-click opens the admin
                    console in a new tab. SpaLink swallows the plain click for
                    SPA navigation, which also stops Radix's own select handler
                    from firing — so we close the menu ourselves. */}
                {isAdmin && (
                  <DropdownMenuItem className="sidenav-menu-item" asChild>
                    <SpaLink href="/admin" onClick={() => setMenuOpen(false)}>
                      Admin console
                    </SpaLink>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="sidenav-menu-item"
                  onSelect={() => authn?.logout?.()}
                >
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <button
            type="button"
            className="btn brand"
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => authn?.login?.()}
          >Sign in</button>
        )}
      </div>
    </aside>
  );
}

// ─── Bot picker popover ───────────────────────────────────────────────
//
// A searchable, scrollable list of every bot behind a caller-supplied trigger
// (currently the bot-crumb chevron). The panel is portaled to <body> and
// positioned under the trigger so the sidebar's own `overflow` can't clip it.
// Closes on outside click, Escape, or selection; page/sidebar scroll keeps it
// pinned under the trigger, while scrolling its own list leaves it untouched.
// Each row is a real link — see the `hrefFor` prop.

function BotPickerPopover({
  bots, onPick, hrefFor, triggerClassName, triggerAriaLabel, children,
}: {
  bots: NavNode[];
  onPick: (botId: string) => void;
  /** Where each row points. Rendered as a real `href` so Cmd/Ctrl-click,
   *  middle-click and "Open link in new tab" all work. */
  hrefFor: (botId: string) => string;
  triggerClassName: string;
  triggerAriaLabel?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setRect({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 220) });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // Keep the fixed panel pinned under the trigger as the page/sidebar scrolls.
    // Scrolls that originate *inside* the panel (its own list) are ignored — they
    // must not move or close it. Capture phase so it also sees the sidebar's
    // inner scroll container.
    const reposition = (e: Event) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      setRect({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 220) });
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={triggerClassName + (open ? ' active' : '')}
        aria-label={triggerAriaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {children}
      </button>
      {open && rect && createPortal(
        <div
          ref={panelRef}
          className="sidenav-more-panel"
          style={{ position: 'fixed', left: rect.left, top: rect.top, width: rect.width }}
          role="listbox"
        >
          <Command>
            <CommandInput placeholder="Search bots…" autoFocus />
            <CommandList>
              <CommandEmpty>No bots found.</CommandEmpty>
              {bots.map((bot) => {
                const id = bot.id.replace(/^bot:/, '');
                return (
                  <CommandItem
                    key={bot.id}
                    value={`${bot.label} ${id}`}
                    onSelect={() => { setOpen(false); onPick(id); }}
                  >
                    {/* The row's body is a real link. A plain click is
                        suppressed here and left to bubble into cmdk's own
                        click handler → `onSelect` above → SPA navigation. A
                        modified click (Cmd/Ctrl/Shift/Alt) is handed to the
                        browser — it opens a new tab and must NOT reach cmdk,
                        or this tab would switch bots too. The panel stays
                        open so another modified click can open another bot.
                        Middle-click never fires `click` at all, so it opens
                        a new tab on its own. cmdk overwrites an `onClick`
                        passed to CommandItem, which is why the handler lives
                        on the link rather than on the item. */}
                    <a
                      href={hrefFor(id)}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
                          e.stopPropagation();
                          return;
                        }
                        e.preventDefault();
                      }}
                    >
                      <span className={('nav-av ' + botTint(id)).trim()} aria-hidden="true">{bot.avatar || 'B'}</span>
                      <span className="nav-label">{bot.label}</span>
                    </a>
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>
        </div>,
        document.body,
      )}
    </>
  );
}

// ─── Workflows level ──────────────────────────────────────────────────
//
// The bot's conversation workflows, one nest below Settings. This replaced the
// floating panel that used to sit on the canvas itself (`.cgx-workflow-sidebar`).
// Rows are plain links — opening a workflow is a `?wf=` URL change that
// `Configure` feeds back into the editor. The two buttons are canvas actions and
// come from `workflowNav`, published by the open canvas; with no canvas mounted
// (the tab is lazy) there is nothing to act on, so the list says it's loading.

/**
 * Amber dot on a sidebar row whose bot (or workflow) has unpublished changes.
 * Reads the persisted drafts, so it also marks bots this tab never opened.
 */
function DraftDot({ title }: { title: string }) {
  return (
    <span className="nav-draft-dot" role="img" aria-label={title} title={title} />
  );
}

function BotDraftDot({ botId }: { botId: string }) {
  const drafts = useBotDraftState(botId);
  return drafts.any ? <DraftDot title="Unpublished changes" /> : null;
}

function WorkflowLevelBody({ botId }: { botId: string }) {
  const wfNav = useWorkflowNav();
  const workflows = orderWorkflowsForNav(wfNav?.workflows ?? []);
  const ready = !!wfNav?.loaded;
  const drafts = useBotDraftState(botId);

  return (
    <nav className="sidenav-items main-nav-items wf-nav-items" aria-label="Workflows">
      <div className="nav-section top-level wf-nav-head">
        <span>Workflows</span>
        <button
          type="button"
          className="wf-nav-create"
          onClick={() => wfNav?.createWorkflow()}
          disabled={!wfNav}
          aria-label="Create workflow"
          title="Create workflow"
        >
          <Plus size={14} strokeWidth={2.4} aria-hidden="true" />
        </button>
      </div>
      {!ready && <div className="wf-nav-empty">Loading workflows…</div>}
      {ready && workflows.length === 0 && (
        <div className="wf-nav-empty">No workflows yet. Create one to get started.</div>
      )}
      {workflows.map((wf) => {
        const open = wf.id === wfNav?.activeWorkflowID;
        return (
          <div key={wf.id} className="wf-nav-row">
            <SpaLink
              href={workflowEditorHref(botId, wf.id)}
              className={'nav-link main-nav-link nested wf-nav-open' + (open ? ' active' : '')}
              title={wf.description || `Open ${wf.name}`}
            >
              <span className="nav-label">{wf.name}</span>
              {drafts.workflowIDs.includes(wf.id) && <DraftDot title="Unpublished changes" />}
              <span className="wf-nav-meta">
                {wf.node_count} step{wf.node_count === 1 ? '' : 's'}
                {wf.head_version > 0 ? ` · v${wf.head_version}` : ' · draft'}
              </span>
            </SpaLink>
            {/* Revealed on row hover/focus (see .wf-nav-row in soft.css). Adds a
                workflow_call node for `wf` to the graph that's open — so it's
                disabled on the open workflow, which would be a self-call. */}
            <button
              type="button"
              className="wf-nav-add-node"
              onClick={() => wfNav?.insertWorkflowCall(wf)}
              disabled={!wfNav || open}
              aria-label={`Add ${wf.name} as a node`}
            >
              <Network size={13} strokeWidth={2.1} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </nav>
  );
}

// ─── Call logs level ──────────────────────────────────────────────────
//
// View switcher + facet filters. Every row is a link into the same page with a
// different query string (see nav/callLogsUrl.ts) — clicking an active filter
// clears it, and "Clear filters" drops them all.

const OUTCOME_ROWS: Array<{ value: CallLogsParams['outcome']; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'unresolved', label: 'Unresolved' },
];

const SENTIMENT_ROWS: Array<{ value: CallLogsParams['sentiment']; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
];

/** Twemoji glyphs for the outcome/sentiment filter rows. "All" gets an empty
 *  same-size slot so every row's label stays aligned. */
const FILTER_EMOJI: Record<string, React.ReactNode> = {
  all: <span className="cl-filter-emoji" aria-hidden="true" />,
  resolved: (
    <svg className="cl-filter-emoji" viewBox="0 0 36 36"><circle fill="#78B159" cx="18" cy="18" r="18"/></svg>
  ),
  unresolved: (
    <svg className="cl-filter-emoji" viewBox="0 0 36 36"><circle fill="#DD2E44" cx="18" cy="18" r="18"/></svg>
  ),
  positive: (
    <svg className="cl-filter-emoji" aria-hidden="true" viewBox="0 0 36 36"><circle fill="#FFCC4D" cx="18" cy="18" r="18" /><path fill="#664500" d="M18 21c-3.623 0-6.027-.422-9-1-.679-.131-2 0-2 2 0 4 4.595 9 11 9 6.404 0 11-5 11-9 0-2-1.321-2.132-2-2-2.973.578-5.377 1-9 1z" /><path fill="#FFF" d="M9 22s3 1 9 1 9-1 9-1-2 4-9 4-9-4-9-4z" /><ellipse fill="#664500" cx="12" cy="13.5" rx="2.5" ry="3.5" /><ellipse fill="#664500" cx="24" cy="13.5" rx="2.5" ry="3.5" /></svg>
  ),
  neutral: (
    <svg className="cl-filter-emoji" aria-hidden="true" viewBox="0 0 36 36"><path fill="#FFCC4D" d="M36 18c0 9.941-8.059 18-18 18-9.94 0-18-8.059-18-18C0 8.06 8.06 0 18 0c9.941 0 18 8.06 18 18" /><ellipse fill="#664500" cx="11.5" cy="16.5" rx="2.5" ry="3.5" /><ellipse fill="#664500" cx="24.5" cy="16.5" rx="2.5" ry="3.5" /><path fill="#664500" d="M25 26H11c-.552 0-1-.447-1-1s.448-1 1-1h14c.553 0 1 .447 1 1s-.447 1-1 1z" /></svg>
  ),
  negative: (
    <svg className="cl-filter-emoji" aria-hidden="true" viewBox="0 0 36 36"><path fill="#FFCC4D" d="M36 18c0 9.941-8.059 18-18 18-9.94 0-18-8.059-18-18C0 8.06 8.06 0 18 0c9.941 0 18 8.06 18 18" /><path fill="#664500" d="M16.664 10.543c-.207-.09-.451-.03-.592.148-.009.012-.979 1.202-3.335 1.844-.775.212-1.531.319-2.245.319-.992 0-1.559-.212-1.562-.213-.21-.085-.453-.014-.587.169-.135.184-.128.435.016.612.071.087 1.78 2.147 4.125 2.147.351 0 .702-.047 1.043-.14 2.854-.778 3.41-4.208 3.432-4.354.034-.223-.087-.441-.295-.532zm10.975 2.155c-.138-.181-.38-.245-.59-.159-.006.003-.633.256-1.737.256-.661 0-1.358-.089-2.073-.265-2.369-.583-3.369-1.749-3.377-1.758-.146-.176-.388-.233-.596-.137-.207.095-.323.318-.283.542.026.145.668 3.56 3.539 4.267.311.077.629.116.946.116 2.396 0 4.099-2.16 4.17-2.252.138-.181.137-.43.001-.61zM9.998 10c-.448 0-.855-.303-.969-.757-.133-.535.191-1.077.724-1.212.036-.009 3.593-.961 5.352-4.478.247-.494.846-.695 1.342-.447.494.247.694.848.447 1.342-2.181 4.361-6.471 5.477-6.652 5.523-.081.02-.163.029-.244.029zM26 10c-.08 0-.161-.01-.242-.03-.182-.045-4.472-1.162-6.652-5.523-.247-.494-.047-1.095.447-1.342.493-.247 1.094-.047 1.342.447 1.758 3.517 5.315 4.469 5.352 4.478.533.137.857.681.722 1.215-.116.453-.522.755-.969.755zm-8 18.965c-3.623 0-6.027.422-9 1-.679.131-2 0-2-2 0-4 4.595-9 11-9 6.404 0 11 5 11 9 0 2-1.321 2.132-2 2-2.973-.578-5.377-1-9-1z" /><path fill="#FFF" d="M10 25s3-1 8-1 8 1 8 1-2-4-8-4-8 4-8 4z" /></svg>
  ),
};

function CallLogsLevelBody({
  botId, params, memoryEnabled,
}: { botId: string; params: CallLogsParams; memoryEnabled: boolean }) {
  const views: Array<{ value: CallLogsParams['view']; label: string }> = [
    { value: 'calls', label: 'Calls' },
    { value: 'analytics', label: 'Analytics' },
    ...(memoryEnabled ? [{ value: 'users' as const, label: 'Users' }] : []),
  ];

  return (
    <>
      <nav className="sidenav-items main-nav-items call-logs-view-nav" aria-label="Call logs view">
        <div className="nav-section top-level">View</div>
        {views.map((v) => (
          <SpaLink
            key={v.value}
            href={callLogsHref(botId, { ...params, view: v.value })}
            className={'nav-link main-nav-link' + (params.view === v.value ? ' active' : '')}
          >
            <NavGlyph id={`call-logs:${v.value}`} />
            <span className="nav-label">{v.label}</span>
          </SpaLink>
        ))}
      </nav>

      <nav className="sidenav-items main-nav-items cl-filter-nav" aria-label="Call logs filters">
        <div className="nav-section top-level">Filters</div>
        <div className="nav-section">Outcome</div>
        {OUTCOME_ROWS.map((o) => {
          const on = params.outcome === o.value;
          return (
            <SpaLink
              key={o.value}
              href={callLogsHref(botId, { ...params, outcome: on ? 'all' : o.value })}
              className={'nav-link main-nav-link nested' + (on ? ' active' : '')}
              aria-pressed={on}
            >
              {FILTER_EMOJI[o.value]}
              <span className="nav-label">{o.label}</span>
            </SpaLink>
          );
        })}
        <div className="nav-section">Sentiment</div>
        {SENTIMENT_ROWS.map((s) => {
          const on = params.sentiment === s.value;
          return (
            <SpaLink
              key={s.value}
              href={callLogsHref(botId, { ...params, sentiment: on ? 'all' : s.value })}
              className={'nav-link main-nav-link nested' + (on ? ' active' : '')}
              aria-pressed={on}
            >
              {FILTER_EMOJI[s.value]}
              <span className="nav-label">{s.label}</span>
            </SpaLink>
          );
        })}
        {hasActiveCallLogsFilters(params) && (
          <SpaLink
            href={callLogsHref(botId, { view: params.view })}
            className="nav-link main-nav-link sidenav-clear-filters"
          >
            <X className="nav-ico" aria-hidden="true" strokeWidth={2.2} />
            <span className="nav-label">Clear filters</span>
          </SpaLink>
        )}
      </nav>
    </>
  );
}

// ─── Admin level ──────────────────────────────────────────────────────
//
// The `/admin` console's sections, grouped exactly as the page dispatches them
// (see nav/adminSections.ts). In-page sections link to `/admin/<id>` paths (old
// `?section=` links still resolve); the billing console is an external route.
// Active row is derived from the URL.

function AdminLevelBody({ pathname, search }: { pathname: string; search: string }) {
  const section = adminSectionFromSearch(search); // back-compat active-state for old ?section= links
  const onAdminRoot = pathname === '/admin' || pathname === '/admin/';
  return (
    <>
      {ADMIN_SECTIONS.map((group) => (
        <nav key={group.id} className="sidenav-items main-nav-items" aria-label={group.label}>
          <div className="nav-section top-level">{group.label}</div>
          {group.leaves.map((leaf) => {
            const active = leaf.href
              ? pathname === leaf.href
              : pathname === `/admin/${leaf.id}`
                || pathname.startsWith(`/admin/${leaf.id}/`)
                || (onAdminRoot && leaf.id === section);
            return (
              <SpaLink
                key={leaf.id}
                href={adminSectionHref(leaf)}
                className={'nav-link main-nav-link' + (active ? ' active' : '')}
              >
                <span className="nav-label">{leaf.label}</span>
              </SpaLink>
            );
          })}
        </nav>
      ))}
    </>
  );
}
